import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h2", {
  class: "text-left text-lg text-textPrimary",
  "data-testid": "activity-attachments-title"
}, " Anexos do Registro de Atividade ", -1)
const _hoisted_2 = { class: "flex h-full flex-col gap-4 lg:flex-row" }
const _hoisted_3 = { class: "flex min-w-0 flex-1 flex-col gap-3" }
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "rounded-lg bg-gray-200 px-2 py-1 text-xl text-textPrimary",
  "data-testid": "activity-attachments-container-id"
}
const _hoisted_6 = { class: "truncate text-xl font-semibold text-textPrimary" }
const _hoisted_7 = {
  key: 0,
  class: "flex flex-col items-start gap-2 rounded border border-negative bg-red-50 p-3 text-left text-sm text-negative",
  "data-testid": "activity-attachments-error",
  role: "alert"
}
const _hoisted_8 = {
  key: 1,
  class: "py-10 text-center text-sm text-gray-500",
  "data-testid": "activity-attachments-loading"
}
const _hoisted_9 = {
  key: 0,
  class: "flex w-full shrink-0 flex-col gap-1 text-left lg:w-[260px]"
}
const _hoisted_10 = { class: "text-base font-bold leading-6 text-textPrimary" }
const _hoisted_11 = { class: "flex flex-col gap-1" }
const _hoisted_12 = ["aria-current", "onClick"]
const _hoisted_13 = { class: "truncate" }
const _hoisted_14 = { class: "flex justify-end" }

import { computed, onBeforeUnmount, ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import type { DSAttendanceActivityAttachment } from '@/types/ds-attendance.types'

/**
 * Anexo de um Registro de Atividade, como a fila e o Detalhes do Plantão listam.
 * O contrato mora em `types/ds-attendance.types.ts` (a listagem devolve os anexos
 * dentro do item do container); aqui fica só o apelido que os consumidores já
 * importam deste arquivo.
 */
export type ActivityAttachment = DSAttendanceActivityAttachment


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityAttachmentsModal',
  props: {
    modelValue: { type: Boolean },
    containerId: {},
    attachments: { default: () => [] }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const selectedIndex = ref(0)
const rotation = ref(0)
const zoom = ref(1)
const magnifier = ref(false)
const isLoading = ref(false)
const loadError = ref<string | null>(null)

// Cache por `evidenceId`: reabrir o mesmo anexo não baixa de novo, e a revogação
// no fechamento tem uma lista única para percorrer.
const objectUrls = ref(new Map<number, { url: string; type: string }>())

const currentAttachment = computed(() => props.attachments[selectedIndex.value] ?? null)
const currentEntry = computed(() =>
  currentAttachment.value ? objectUrls.value.get(currentAttachment.value.evidenceId) : undefined
)
const currentUrl = computed(() => currentEntry.value?.url ?? null)
const currentType = computed(() => currentEntry.value?.type)

/**
 * `blob:` fica preso à aba até ser revogado. O `EvidenceModal` já paga essa
 * disciplina; repetir aqui é obrigatório porque o anexo pode ter 50 MB.
 */
function revokeAll() {
  objectUrls.value.forEach(({ url }) => URL.revokeObjectURL(url))
  objectUrls.value = new Map()
}

onBeforeUnmount(revokeAll)

async function loadCurrent() {
  const attachment = currentAttachment.value
  const containerId = props.containerId
  if (!attachment || containerId == null) return
  if (objectUrls.value.has(attachment.evidenceId)) return

  isLoading.value = true
  loadError.value = null
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(containerId, attachment.evidenceId)
    // `Map` novo (e não `.set` no mesmo): o computed precisa reagir à troca da
    // referência — mutação in-place de um `Map` não dispara render em Vue 3.
    const next = new Map(objectUrls.value)
    next.set(attachment.evidenceId, { url: URL.createObjectURL(blob), type: blob.type })
    objectUrls.value = next
  } catch (error) {
    console.error('Erro ao baixar o anexo do Registro de Atividade:', error)
    loadError.value = getApiErrorMessage(error, 'Não foi possível abrir o anexo.')
  } finally {
    isLoading.value = false
  }
}

function selectAttachment(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
  loadCurrent()
}

function close() {
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      selectedIndex.value = 0
      rotation.value = 0
      zoom.value = 1
      loadError.value = null
      loadCurrent()
      return
    }
    revokeAll()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    "modal-active": _ctx.modelValue,
    "has-header-border": true,
    "has-footer-border": true,
    "max-width": "max-w-6xl",
    "max-height": "90vh",
    onClose: close
  }, {
    header: _withCtx(() => [
      _hoisted_1
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_14, [
        _createVNode(OutlineButton, {
          "data-testid": "activity-attachments-close",
          text: "Fechar",
          width: "100px",
          height: "32px",
          onClick: close
        })
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            (_ctx.containerId)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, "#" + _toDisplayString(_ctx.containerId), 1))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_6, _toDisplayString(currentAttachment.value?.originalFileName ?? 'Sem anexo'), 1)
          ]),
          (loadError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("span", null, _toDisplayString(loadError.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "font-semibold underline",
                  "data-testid": "activity-attachments-retry",
                  onClick: loadCurrent
                }, " Tentar novamente ")
              ]))
            : (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, " Carregando anexo… "))
              : (currentUrl.value)
                ? (_openBlock(), _createBlock(StepPreview, {
                    key: 2,
                    file: null,
                    "file-url": currentUrl.value,
                    "file-type": currentType.value,
                    rotation: rotation.value,
                    zoom: zoom.value,
                    mode: "preview",
                    magnifier: magnifier.value,
                    "magnifier-toggle": "",
                    "show-display-name": false,
                    "onUpdate:rotation": _cache[0] || (_cache[0] = ($event: any) => (rotation.value = $event)),
                    "onUpdate:zoom": _cache[1] || (_cache[1] = ($event: any) => (zoom.value = $event)),
                    "onUpdate:magnifier": _cache[2] || (_cache[2] = ($event: any) => (magnifier.value = $event))
                  }, null, 8, ["file-url", "file-type", "rotation", "zoom", "magnifier"]))
                : _createCommentVNode("", true)
        ]),
        (_ctx.attachments.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("span", _hoisted_10, "Anexos (" + _toDisplayString(_ctx.attachments.length) + ")", 1),
              _createElementVNode("ul", _hoisted_11, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.attachments, (attachment, index) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: attachment.evidenceId
                  }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass(["flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors", 
                index === selectedIndex.value
                  ? 'bg-primary30 font-semibold text-white'
                  : 'text-textPrimary hover:bg-gray-100'
              ]),
                      "data-testid": "activity-attachment-item",
                      "aria-current": index === selectedIndex.value ? 'true' : undefined,
                      onClick: ($event: any) => (selectAttachment(index))
                    }, [
                      _createElementVNode("span", _hoisted_13, _toDisplayString(attachment.originalFileName), 1)
                    ], 10, _hoisted_12)
                  ]))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["modal-active"]))
}
}

})